import { motion, useReducedMotion } from 'framer-motion'

/**
 * Motion primitives — thin wrappers around framer-motion for scroll-triggered
 * reveals and stagger layouts. All primitives respect prefers-reduced-motion
 * (they render plain content without animation when set).
 */

export function FadeIn({ children, delay = 0, y = 24, className = '', as = 'div' }) {
  const reduce = useReducedMotion()
  const MotionTag = motion[as] || motion.div
  if (reduce) {
    const Tag = as
    return <Tag className={className}>{children}</Tag>
  }
  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </MotionTag>
  )
}

export function Stagger({ children, className = '', delayChildren = 0.1, staggerChildren = 0.1 }) {
  const reduce = useReducedMotion()
  if (reduce) {
    return <div className={className}>{children}</div>
  }
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-80px' }}
      variants={{
        hidden: {},
        show: { transition: { delayChildren, staggerChildren } },
      }}
    >
      {children}
    </motion.div>
  )
}

export function StaggerItem({ children, className = '', y = 24 }) {
  const reduce = useReducedMotion()
  if (reduce) {
    return <div className={className}>{children}</div>
  }
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y },
        show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
      }}
    >
      {children}
    </motion.div>
  )
}

/**
 * MagneticCTA — wraps a child element and adds a slight cursor-pull effect on hover.
 * Falls back to no-op on touch / reduced-motion.
 *
 * Use with a single child (e.g., a button or link).
 */
export function MagneticCTA({ children, strength = 0.25 }) {
  const reduce = useReducedMotion()
  if (reduce) {
    return children
  }
  const handleMove = (e) => {
    const target = e.currentTarget
    const rect = target.getBoundingClientRect()
    const x = (e.clientX - rect.left - rect.width / 2) * strength
    const y = (e.clientY - rect.top - rect.height / 2) * strength
    target.style.transform = `translate(${x}px, ${y}px)`
  }
  const handleLeave = (e) => {
    e.currentTarget.style.transform = 'translate(0, 0)'
  }
  return (
    <span
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ display: 'inline-block', transition: 'transform 0.18s ease-out' }}
    >
      {children}
    </span>
  )
}
